import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';

// Components
import { GIButton } from 'app-components';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import TicketManagementConstant from '../TicketManagementConstant';

const CustomFieldForm = props => {
    const {
        initialFormValues,
        onCancelHandler,
        addCustomFields,
        updateCustomFields,
        isEdit,
        customTypeOptions,
        submitBtnText,
        isDuplicate,
    } = props;
    const intl = useIntl();

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm({ defaultValues: initialFormValues, mode: 'onBlur' });

    const watchType = watch('type', initialFormValues.type);

    const DESCRIPTION_LENGTH = {
        min: 5,
        max: 250,
    };

    const onSubmit = data => {
        if (data.type !== customTypeOptions[1].value) {
            delete data['values'];
        } else {
            data.values = data.values
                .split(TicketManagementConstant.SPLIT_AT_LINE_BREAK_REG_EX)
                .filter(function (entry) {
                    return entry.trim() != '';
                });
        }
        isEdit ? updateCustomFields(data) : addCustomFields(data);
    };

    const handleCancel = e => {
        e.preventDefault();
        onCancelHandler();
    };

    const splitValuesAndReturnList = optionValues => {
        //Spliting the text where line break appears and creating an array of option values
        return optionValues
            .split(TicketManagementConstant.SPLIT_AT_LINE_BREAK_REG_EX)
            .map(value => value.toLowerCase());
    };

    const checkDuplicateValues = optionValues => {
        const lowercasedValues = splitValuesAndReturnList(optionValues);
        const uniqueValues = new Set(lowercasedValues);
        return lowercasedValues.length === uniqueValues.size;
    };

    const checkMaxLength = optionValues => {
        let count = 0;
        const valuesEntered = splitValuesAndReturnList(optionValues);
        valuesEntered.forEach(value => {
            if (value.length > TicketManagementConstant.OPTION_LIST_MAX_ROW_LENGTH) {
                count++;
            }
        });
        return count < 1;
    };

    const onChangeOptionList = event => {
        const isValid = checkMaxLength(event.target.value);
        if (!isValid) {
            setError('values', {
                type: 'maxLengthCheck',
                message: 'Option value can not be more than 100 characters.',
            });
        } else {
            clearErrors('values');
        }
    };

    const noZeroOnFirstCharacter = str => {
        if (!TicketManagementConstant.FIELD_NAME_REG_EX.test(str)) {
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (!isEdit) {
            setValue('values', '');
        }

        if (watchType !== customTypeOptions[1].value) {
            clearErrors('values');
        }
    }, [watchType, isEdit]);

    return (
        <form className="ticket-form" id="ticketForm" onSubmit={handleSubmit(onSubmit)}>
            {isDuplicate && (
                <AlertMessage
                    content={<FormattedMessage id="ticket_management.custom_field.duplicate_field" />}
                    type="error"
                />
            )}
            <label className="ticket-form__label display--block">
                <FormattedMessage id="ticket_management.custom_field.form_header.type" />
                <select
                    className={'ticket-form__select'}
                    disabled={isEdit}
                    name="type"
                    {...register('type', {
                        required: {
                            value: true,
                            message: 'Please select a value.',
                        },
                    })}
                >
                    {customTypeOptions.map((option, index) => {
                        const { val = option.value, label = option.text } = option;
                        return (
                            <option key={index} value={val}>
                                {label}
                            </option>
                        );
                    })}
                </select>
                {errors.type && <p className="shown error--msg">{errors.type.message}</p>}
            </label>
            
            <label className="ticket-form__label display--block">
                <span className='required-true'>
                    <FormattedMessage id="ticket_management.custom_field.form_header.name" />
                </span>
                <input
                    type="text"
                    className={'ticket-form__input'}
                    name="name"
                    maxLength={30}
                    {...register('name', {
                        required: {
                            value: true,
                            message: 'Field Name is required.',
                        },
                        validate: {
                            noZeroOnFirstCharacter: optionValues =>
                                noZeroOnFirstCharacter(optionValues) ||
                                intl.formatMessage({ id: 'ticket_management.custom_field.form_header.name.error' }),
                        },
                    })}
                />
                {errors.name && <p className="shown error--msg">{errors.name.message}</p>}
            </label>

            <label className="ticket-form__label display--block">
                <FormattedMessage id="ticket_management.custom_field.form_header.description" />
                <textarea
                    type="text"
                    className={'ticket-form__input'}
                    name="description"
                    rows={4}
                    {...register('description', {
                        maxLength: {
                            value: DESCRIPTION_LENGTH.max,
                            message: (
                                <FormattedMessage id="ticket_management.custom_field_form.validation.description.maxlength" />
                            ),
                        },
                    })}
                />
                {errors.description && <p className="shown error--msg">{errors.description.message}</p>}
            </label>

            {watchType === customTypeOptions[1].value && (
                <label className="ticket-form__label display--block">
                    <span className='required-true'>
                        <FormattedMessage id="ticket_management.custom_field.form_header.list" />
                    </span>
                    <textarea
                        className={'ticket-form__input'}
                        rows={4}
                        name="values"
                        {...register('values', {
                            required: {
                                value: true,
                                message: 'Option value is required.',
                            },
                            validate: {
                                duplicateValues: optionValues =>
                                    checkDuplicateValues(optionValues) || 'Duplicate option values found.',
                                maxLengthCheck: optionValues =>
                                    checkMaxLength(optionValues) || 'Option value can not be more than 100 characters.',
                            },
                            onChange: event => onChangeOptionList(event),
                        })}
                    ></textarea>
                    {errors.values && <p className="shown error--msg">{errors.values.message}</p>}
                </label>
            )}

            <div className="ticket-form__controls">
                <GIButton id="addNewTicketCategory" labelId="button.cancel" onClickHandler={handleCancel} />
                <GIButton
                    id="updateNewTicketCategory"
                    labelId={submitBtnText}
                    onClickHandler={handleSubmit(onSubmit)}
                />
            </div>
        </form>
    );
};

export default CustomFieldForm;
